import React, { ReactElement, memo, useEffect, useMemo } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  getFilteredRowModel,
  getExpandedRowModel,
  VisibilityState,
} from '@tanstack/react-table';
import './table.css';
import { TableColumn, formatColumns } from './use-column';
import Filter from './filter';
import Footer from './pagination';
import useExpandable from './use-expandable';
import { CustomExpandContent } from './expand';
import { OpenExpandContent } from './open-expand';
import RowSelectionProps from './props/row-selection.props';
import useRowSelection from './use-row-selection';
import clsx from 'clsx';

interface ExpandProps<T> {
  rowExpandable: (row: T) => boolean;
  expandedRowRender: (row: T, expanded: boolean) => ReactElement;
}
interface RowSelectionProps {}
export interface TableProps<T = any> {
  columns: TableColumn<T>[];
  dataSource: T[];
  expandable?: ExpandProps<T>;
  enableRowSelection?: RowSelectionProps;
  className?: string;
  pageData?: any;
  updatePageNumber?: any;
  updatePageSize?: any;
  setSorting?: any;
  sorting?: any;
  setColumnFilters?: any;
  columnFilters?: any;
  expanded?: any;
  setExpanded?: any;
  openExpand?: boolean;
  size?: 'normal' | 'large' | 'condensed' | 'striped';
  noDataDes?: string | ReactElement;
  manualSorting?: boolean;
  showDropDown?: boolean;
  selectFunc?: any;
  rowSelection?: any;
  setRowSelection?: any;
  pageOptions?: Array<number>;
  setRowSelectionFunc?: any;
  showOnlyHeader?: boolean;
  getRowId?: (originalRow: T, index: number) => string;
}

function Table<T = any>(props: TableProps<T>) {
  const {
    dataSource = [],
    enableRowSelection: customRowSelection,
    expandable: customExpandable,
    className,
    pageData,
    updatePageNumber,
    updatePageSize,
    sorting,
    setSorting,
    setColumnFilters,
    columnFilters,
    expanded,
    setExpanded,
    openExpand = false,
    size = 'normal',
    noDataDes,
    manualSorting,
    showDropDown = true,
    selectFunc,
    rowSelection,
    setRowSelection,
    pageOptions,
    setRowSelectionFunc,
    showOnlyHeader = false,
    getRowId,
  } = props;
  let rowSelectionVal;
  if (typeof customRowSelection === 'undefined') {
    rowSelectionVal = {
      selectedRowKeys: null,
    };
  } else {
    rowSelectionVal = { ...customRowSelection };
  }
  const { setSelectedRowKeys, customSelectChange, customSetSelectedRowKeys } = useRowSelection(rowSelectionVal);
  let expandable;
  if (typeof customExpandable === 'undefined') {
    expandable = {
      rowExpandable: (row) => {
        return false;
      },
    };
  } else {
    expandable = customExpandable;
  }
  const { setRowExpandable } = useExpandable(expandable);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  React.useEffect(() => {
    customSetSelectedRowKeys(setRowSelection);
  }, [customRowSelection]);

  const memoTableColumns = useMemo(() => {
    return formatColumns(props);
  }, [props]);

  const table = useReactTable({
    data: dataSource ?? [],
    columns: memoTableColumns,
    state: {
      sorting,
      columnFilters,
      expanded,
      rowSelection,
      columnVisibility: columnVisibility,
    },
    onExpandedChange: setExpanded,
    onRowSelectionChange: (selectRows) => {
      setSelectedRowKeys(() => setRowSelection(selectRows));
      customSelectChange(table.getSelectedRowModel().rows.map((row) => row.original));
      selectFunc(selectRows);
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: (row) => setRowExpandable(row),
    manualSorting: manualSorting,
    onColumnVisibilityChange: setColumnVisibility,
    enableRowSelection: setRowSelectionFunc,
    getRowId: (row: any, index) => {
      if (getRowId) {
        return getRowId(row, index);
      }
      return row?.id;
    },
  });

  useEffect(() => {
    const visibleColumnIds = table
      .getAllColumns()
      .filter((column) => !column.columnDef.enableHiding)
      .map((column) => column.id);
    const allHideableColumnIds = table
      .getAllColumns()
      .filter((column) => column.getCanHide())
      .map((column) => column.id);
    const initialColumnVisibility: VisibilityState = allHideableColumnIds.reduce((visibilityState, columnId) => {
      visibilityState[columnId] = visibleColumnIds.includes(columnId);
      return visibilityState;
    }, {} as VisibilityState);
    setColumnVisibility(initialColumnVisibility);
  }, []);

  const TableCls = clsx({
    'c-table': true,
    [`c-table--${size}`]: size !== 'normal',
    [className]: !!className,
  });

  const paginationData = {
    page: pageData ? pageData : {},
    pageChange: updatePageNumber,
    pageSizeChange: updatePageSize,
    showDropDown: showDropDown,
    pageOptions: pageOptions,
  };

  const isNoData = table.getRowModel()?.rows?.length === 0;

  const renderNoDataComponent = () => {
    const hasNoDataDes = 'noDataDes' in props;

    if (!hasNoDataDes) {
      return 'No Data';
    }

    return noDataDes;
  };
  return (
    <div className="md:overflow-x-auto sm:overflow-x-auto">
      <table className={TableCls} role="table">
        <thead role="thead">
          {table.getHeaderGroups().map((headerGroup, index) => (
            <tr key={index}>
              {headerGroup.headers.map((header) => {
                const isDefaultSize = header.column.getSize() === 150;
                const columnProps = header.column.columnDef as any;
                const columnStyle = { ...columnProps?.style };
                if (!isDefaultSize) {
                  columnStyle.width = header.column.getSize();
                }
                return (
                  <th key={header.id} colSpan={header.colSpan} role="columnheader" style={columnStyle}>
                    {header.isPlaceholder ? null : (
                      <>
                        <div
                          className={header.column.getCanSort() ? 'column_can_sort' : ''}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {header.column.columnDef.enableColumnFilter ? (
                            <Filter column={header.column} />
                          ) : header.column.columnDef.header !== '-' ? (
                            flexRender(header.column.columnDef.header, header.getContext())
                          ) : (
                            ''
                          )}
                          {header.column.columnDef.enableSorting && <span className="table_sort" />}
                          {{
                            asc: <span className="table_sort asc" />,
                            desc: <span className="table_sort desc" />,
                          }[header.column.getIsSorted().toString()] ?? null}
                        </div>
                      </>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        {!showOnlyHeader && (
          <tbody role="rowgroup">
            {isNoData && (
              <tr>
                <td colSpan={memoTableColumns.length} align="center">
                  {renderNoDataComponent()}
                </td>
              </tr>
            )}
            {!isNoData &&
              table.getRowModel().rows.map((row) => {
                return (
                  <React.Fragment key={row.id}>
                    <tr role="row">
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <td key={cell.id} role="cell">
                            <MemoCell cell={cell} />
                          </td>
                        );
                      })}
                    </tr>
                    {/* When openExpand is true, the expand content will show always.  */}
                    {openExpand ? (
                      <OpenExpandContent row={row} expandable={customExpandable} />
                    ) : (
                      <CustomExpandContent row={row} expandable={customExpandable} />
                    )}
                  </React.Fragment>
                );
              })}
          </tbody>
        )}
      </table>
      {pageData && !isNoData && <Footer data={paginationData}></Footer>}
    </div>
  );
}

function RenderCell({ cell }) {
  const cellComponent = useMemo(() => {
    return cell.column.columnDef.cell;
  }, [cell.id]);

  return flexRender(cellComponent, cell.getContext());
}

const MemoCell = memo(RenderCell);

export { Table };
